import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import { ArrowSimple, CopyToClipboardTooltip, Tooltip, t } from '@stokr/components-library'
import { figureTooltip } from 'components/ui/investmentCard/InvestmentCard'
import { useHeroMetrics, useKeyTerms, type HeroMetric } from 'hooks/sto/useHeroMetrics'
import type { SectionState } from 'models/stoSection'
import { InfoIcon } from 'components/sto/Section'
import { CopyIcon } from 'components/ui/icons'
import {
  AnchorTag,
  Cell,
  Chevron,
  Entries,
  Entry,
  EntryLink,
  EntryText,
  EntryTitle,
  FillerCell,
  Grid,
  Label,
  LabelRow,
  LabelTooltip,
  Note,
  Panel,
  PanelClip,
  PanelInner,
  Placeholder,
  Root,
  Toggle,
  TRANSITION_MS,
  Value,
  ValueLink,
  ValueRow,
} from './HeroMetrics.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const isExpandable = (metric: HeroMetric): boolean => metric.entries.length > 0

/** The shareable link to an anchor on this page. Only read on click, so SSR's missing origin never renders. */
const anchorUrl = (pathname: string, anchor: string): string =>
  `${typeof window === 'undefined' ? '' : window.location.origin}${pathname}#${anchor}`

const AnchorLink = ({ anchor, label }: { anchor: string; label: string }) => {
  const { pathname } = useLocation()
  const trackSto = useStoTrack()
  return (
    <CopyToClipboardTooltip
      value={anchorUrl(pathname, anchor)}
      idleLabel={t('stoPage.anchorLink.copy')}
      copiedLabel={t('stoPage.anchorLink.copied')}>
      <AnchorTag data-cy={`sto-anchor-copy-${anchor}`} onClick={() => trackSto('sto_anchor_link_copied', { anchor })}>
        {label}
        <CopyIcon width={12} height={12} aria-hidden="true" />
      </AnchorTag>
    </CopyToClipboardTooltip>
  )
}

const PanelContent = ({ metric, cyPrefix }: { metric: HeroMetric; cyPrefix: string }) => {
  const trackSto = useStoTrack()
  return (
    <>
      <Entries>
        {metric.entries.map((entry, index) => (
          // Entries have no id of their own; position is their identity.
          <Entry key={index}>
            <EntryTitle>
              {entry.title}
              {entry.tooltip && (
                <Tooltip placement="top" content={figureTooltip(entry.tooltip, entry)}>
                  <InfoIcon role="button" tabIndex={0} aria-label={t('stoPage.keyInformation.moreInfo')} />
                </Tooltip>
              )}
            </EntryTitle>
            <EntryText>
              {entry.href ? (
                <EntryLink
                  href={entry.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cy={`${cyPrefix}-entry-link-${metric.id}-${index}`}
                  onClick={() => trackSto('sto_metric_link_clicked', { metric: metric.id, entry: entry.title })}>
                  {/* A row with a link but no text shows the link itself, never an empty one. */}
                  {entry.text || entry.href}
                </EntryLink>
              ) : (
                entry.text
              )}
            </EntryText>
          </Entry>
        ))}
      </Entries>
      {metric.note && <Note>{metric.note}</Note>}
      {metric.anchor && <AnchorLink anchor={metric.anchor} label={metric.id.replace(/-/g, ' ')} />}
    </>
  )
}

interface MetricGridProps {
  state: SectionState<HeroMetric[]>
  /** Cells per row at full width; more wrap onto further rows. Defaults to all in one row. */
  columns?: number
  /** Which grid, for analytics and `data-cy`. */
  location: 'hero' | 'key_terms'
}

/** Rows of metric cells with a shared expandable panel: hero metrics, key terms, dividend figures. */
export const MetricGrid = ({ state, columns: maxColumns, location }: MetricGridProps) => {
  const { hash } = useLocation()
  const baseId = useId()
  const [openId, setOpenId] = useState<string | null>(null)
  // Outlives openId so the panel keeps its content while it animates closed.
  const [shownId, setShownId] = useState<string | null>(null)
  const handledHash = useRef('')
  const trackSto = useStoTrack()
  const cyPrefix = `sto-${location.replace('_', '-')}-metric`

  const metrics = state.status === 'ready' ? state.data : []

  // `#metric-<key>` opens that metric; useStoAnchors does the scrolling. An effect, not
  // initial state: the server has no hash, and the first client render must match it.
  useEffect(() => {
    if (hash === handledHash.current) return
    const target = metrics.find((metric) => metric.anchor && `#${metric.anchor}` === hash && isExpandable(metric))
    if (!target) return
    handledHash.current = hash
    setOpenId(target.id)
    setShownId(target.id)
  }, [hash, metrics])

  // Once closed, the collapsed panel goes back after the last row (wait for its close animation),
  // so it never leaves an extra pixel between rows.
  useEffect(() => {
    if (openId !== null || shownId === null) return undefined
    const timer = window.setTimeout(() => setShownId(null), TRANSITION_MS)
    return () => window.clearTimeout(timer)
  }, [openId, shownId])

  if (state.status === 'hidden') return null
  if (state.status === 'loading') return <Placeholder aria-hidden="true" />

  const panelId = `${baseId}-panel`
  const isOpen = openId !== null
  const shown = metrics.find((metric) => metric.id === shownId)

  // Cells take even `order`s; the panel takes the odd one right after the last cell of the
  // shown cell's row, in each of the three layouts.
  const count = metrics.length
  const columns = Math.min(count, maxColumns ?? count)
  // "Pairs" is two columns, or one when there is a single column anyway (matches Grid).
  const pairColumns = Math.min(columns, 2)
  const shownIndex = shown ? metrics.indexOf(shown) : count - 1
  const rowEnd = (perRow: number) => 2 * Math.min(count - 1, Math.floor(shownIndex / perRow) * perRow + perRow - 1) + 1
  const panelOrder = {
    '--panel-order': rowEnd(columns),
    '--panel-order-2': rowEnd(pairColumns),
    '--panel-order-1': rowEnd(1),
  } as CSSProperties
  // Figma closes the last row with empty cells, in the full row and in pairs.
  const rowFillers = (columns - (count % columns)) % columns
  const pairFillers = (pairColumns - (count % pairColumns)) % pairColumns

  const toggle = (id: string) => {
    trackSto('sto_metric_toggled', { location, metric: id, open: openId !== id })
    if (openId === id) {
      setOpenId(null)
      return
    }
    setOpenId(id)
    setShownId(id)
  }

  return (
    <Root $open={isOpen}>
      <Grid $columns={columns}>
        {metrics.map((metric, index) => {
          const expandable = isExpandable(metric)
          const active = openId === metric.id
          const labelId = `${baseId}-label-${index}`
          const valueId = `${baseId}-value-${index}`

          return (
            <Cell
              key={metric.id}
              id={metric.anchor}
              style={{ order: 2 * index }}
              $active={active}
              $dimmed={isOpen && !active}
              $expandable={expandable}>
              <LabelRow>
                <Label id={labelId}>{metric.label}</Label>
                {metric.tooltip && (
                  <LabelTooltip>
                    <Tooltip placement="top" content={figureTooltip(metric.tooltip, metric)}>
                      <InfoIcon role="button" tabIndex={0} aria-label={t('stoPage.keyInformation.moreInfo')} />
                    </Tooltip>
                  </LabelTooltip>
                )}
              </LabelRow>

              <ValueRow>
                {metric.href ? (
                  <ValueLink
                    id={valueId}
                    href={metric.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cy={`${cyPrefix}-link-${metric.id}`}
                    onClick={() => trackSto('sto_metric_link_clicked', { location, metric: metric.id })}>
                    {metric.value}
                  </ValueLink>
                ) : (
                  <Value id={valueId}>{metric.value}</Value>
                )}
                {expandable && (
                  <Toggle
                    type="button"
                    aria-expanded={active}
                    aria-controls={panelId}
                    aria-labelledby={`${labelId} ${valueId}`}
                    data-cy={`${cyPrefix}-toggle-${metric.id}`}
                    onClick={() => toggle(metric.id)}>
                    <Chevron $open={active} aria-hidden="true">
                      <ArrowSimple width={10} height={6} />
                    </Chevron>
                  </Toggle>
                )}
              </ValueRow>
            </Cell>
          )
        })}

        {/* The last cell's order: they follow it in source order, still ahead of the panel. */}
        {Array.from({ length: Math.max(rowFillers, pairFillers) }, (_, index) => (
          <FillerCell
            key={index}
            aria-hidden="true"
            $columns={columns}
            $inRow={index < rowFillers}
            $inPairs={index < pairFillers}
            style={{ order: 2 * (count - 1) }}
          />
        ))}

        <Panel
          id={panelId}
          $columns={columns}
          role="region"
          aria-labelledby={shown ? `${baseId}-label-${metrics.indexOf(shown)}` : undefined}
          style={panelOrder}
          $open={isOpen}
          inert={!isOpen}>
          <PanelClip $open={isOpen}>
            {shown && (
              // The panel is the region; the animated wrapper is not a tab panel.
              <PanelInner activeKey={shown.id} appear role="presentation">
                <PanelContent metric={shown} cyPrefix={cyPrefix} />
              </PanelInner>
            )}
          </PanelClip>
        </Panel>
      </Grid>
    </Root>
  )
}

export const HeroMetrics = () => <MetricGrid state={useHeroMetrics()} location="hero" />

export const KeyTerms = () => <MetricGrid state={useKeyTerms()} location="key_terms" />
